import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Toggle } from "@/admin/ui";
import { toast } from "sonner";
import { Sparkles, Loader2, GripVertical } from "lucide-react";

const FaqAdmin = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai/faq", { count: 8 });
      toast.success(`${data.created.length} drafts added — review & publish`);
      load();
    } catch { toast.error("Could not draft"); }
    setBusy(false);
  };
  const add = async () => { await api.post("/admin/faqs", { question: "New question", answer: "", published: false }); load(); };
  const publishAll = async () => { try { const { data } = await api.post("/admin/faqs/publish-all"); toast.success(`Published ${data.published}`); load(); } catch { toast.error("Failed"); } };
  const patch = (id, k, v) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const save = async (f) => { try { await api.put(`/admin/faqs/${f.id}`, f); toast.success("Saved"); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete FAQ?")) return; await api.delete(`/admin/faqs/${id}`); load(); };

  const dragIdx = useRef(null);
  const onDrop = async (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const arr = [...items]; const [m] = arr.splice(from, 1); arr.splice(i, 0, m);
    setItems(arr); dragIdx.current = null;
    try { await api.post("/admin/faqs/reorder", { ids: arr.map((x) => x.id) }); toast.success("Order saved"); }
    catch { toast.error("Reorder failed"); }
  };

  return (
    <div className="max-w-3xl">
      <PageHead title="FAQ" sub="The assistant drafts answers from your catalogue — review, edit and publish. Anything marked [to confirm] needs your input.">
        <Btn variant="ghost" onClick={add} testid="faq-add">+ Add</Btn>
        <Btn variant="ghost" onClick={publishAll} testid="faq-publish-all">Publish all</Btn>
        <Btn onClick={draft} disabled={busy} testid="faq-ai-draft">
          {busy ? <Loader2 size={13} className="animate-spin inline" /> : <Sparkles size={13} className="inline" />} Draft with AI
        </Btn>
      </PageHead>
      {items.length === 0 && <Card><p className="text-espresso/50 text-sm">No FAQs yet. Use "Draft with AI" to generate a starting set from your products.</p></Card>}
      <div className="space-y-4">
        {items.map((f, idx) => (
          <div key={f.id} draggable onDragStart={() => (dragIdx.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)} data-testid={`faq-drag-${f.id}`}>
            <Card>
              <div className="flex items-start gap-3">
                <GripVertical size={16} className="text-espresso/30 mt-2 cursor-move shrink-0" />
                <div className="flex-1">
                  <input value={f.question} data-testid={`faq-q-${f.id}`} onChange={(e) => patch(f.id, "question", e.target.value)}
                    className="w-full bg-ivory border border-espresso/20 px-3 py-2 font-serif text-lg mb-2 outline-none focus:border-espresso" />
                  <textarea value={f.answer} data-testid={`faq-a-${f.id}`} onChange={(e) => patch(f.id, "answer", e.target.value)} rows={4}
                    className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
                  <div className="flex items-center justify-between mt-3">
                    <Toggle label={f.published ? "Published" : "Draft"} value={f.published} onChange={(v) => patch(f.id, "published", v)} testid={`faq-pub-${f.id}`} />
                    <div className="flex gap-4 items-center">
                      <button onClick={() => del(f.id)} className="text-wine text-xs tracking-widest uppercase">Delete</button>
                      <Btn onClick={() => save(f)} testid={`faq-save-${f.id}`}>Save</Btn>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FaqAdmin;
